import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fetchLeaderReportList } from "../services/leaderReportApi";

const DEFAULT_LIMIT = 20;

function validateListParams({ leaderUsername, fromDate, toDate, level }) {
    if (!leaderUsername?.trim()) return "Leader is required";
    if (!fromDate || !toDate) return "Date range is required";
    if (fromDate > toDate) return "From Date cannot be greater than To Date";
    const lvl = Number(level);
    if (!Number.isFinite(lvl) || lvl < 1 || lvl > 6) return "Level must be between 1 and 6";
    return null;
}

/**
 * @param {{
 *   variant: import("../types/leaderReportList").LeaderReportListVariant | null,
 *   open: boolean,
 *   leaderUsername: string,
 *   fromDate: string,
 *   toDate: string,
 *   initialLevel?: number,
 *   onSessionExpired?: () => void,
 * }} options
 */
export function useLeaderReportList({
    variant,
    open,
    leaderUsername,
    fromDate,
    toDate,
    initialLevel = 1,
    onSessionExpired,
}) {
    const [list, setList] = useState([]);
    const [info, setInfo] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [level, setLevel] = useState(initialLevel);
    const [searchInput, setSearchInput] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [pageNo, setPageNo] = useState(1);

    const fetchIdRef = useRef(0);
    const isFirstSearchSync = useRef(true);

    const totalPages = useMemo(() => {
        const total = Number(info?.total || 0);
        const limit = Number(info?.limit || DEFAULT_LIMIT);
        if (!total) return 1;
        return Math.max(1, Math.ceil(total / limit));
    }, [info?.total, info?.limit]);

    const resetState = useCallback(() => {
        setList([]);
        setInfo(null);
        setError(null);
        setLoading(false);
        setLevel(initialLevel);
        setSearchInput("");
        setDebouncedSearch("");
        setPageNo(1);
        isFirstSearchSync.current = true;
    }, [initialLevel]);

    const fetchPage = useCallback(
        async ({ page, searchTerm, levelVal }) => {
            if (!variant || !open) return;

            const err = validateListParams({
                leaderUsername,
                fromDate,
                toDate,
                level: levelVal,
            });
            if (err) {
                setError(err);
                return;
            }

            const token = localStorage.getItem("token") || "";
            if (!token) {
                setError("Token not found. Please login again.");
                onSessionExpired?.();
                return;
            }

            const fetchId = ++fetchIdRef.current;
            setLoading(true);
            setError(null);

            try {
                const out = await fetchLeaderReportList(variant, {
                    token,
                    username: leaderUsername.trim(),
                    from_date: fromDate,
                    to_date: toDate,
                    page_no: page,
                    limit: DEFAULT_LIMIT,
                    search: searchTerm ?? "",
                    level: Number(levelVal),
                });

                if (fetchId !== fetchIdRef.current) return;

                if (out?.error === "session expired") {
                    setError("Session expired");
                    onSessionExpired?.();
                    return;
                }

                if (out?.error === true || (out?.error && out?.error !== false)) {
                    const msg =
                        out?.message ||
                        (typeof out?.error === "string" ? out.error : "Failed to load list");
                    setError(msg);
                    setList([]);
                    return;
                }

                const rows = Array.isArray(out?.list) ? out.list : [];
                const meta = out?.info || {};

                setInfo({
                    total: Number(meta.total || 0),
                    page_no: Number(meta.page_no || page),
                    count: Number(meta.count || rows.length),
                    limit: Number(meta.limit || DEFAULT_LIMIT),
                    has_more: !!meta.has_more,
                });
                setList(rows);
            } catch (e) {
                console.log(e);
                if (fetchId === fetchIdRef.current) {
                    setError("Failed to load list");
                    setList([]);
                }
            } finally {
                if (fetchId === fetchIdRef.current) setLoading(false);
            }
        },
        [variant, open, leaderUsername, fromDate, toDate, onSessionExpired]
    );

    const refresh = useCallback(() => {
        fetchPage({ page: pageNo, searchTerm: debouncedSearch, levelVal: level });
    }, [fetchPage, pageNo, debouncedSearch, level]);

    const onLevelChange = useCallback((nextLevel) => {
        setLevel(Number(nextLevel));
        setPageNo(1);
    }, []);

    const onPrev = useCallback(() => setPageNo((p) => Math.max(1, p - 1)), []);
    const onNext = useCallback(() => setPageNo((p) => Math.min(totalPages, p + 1)), [totalPages]);
    const onJump = useCallback(
        (p) => setPageNo(Math.min(totalPages, Math.max(1, p))),
        [totalPages]
    );

    useEffect(() => {
        if (!open) {
            fetchIdRef.current += 1;
            resetState();
        }
    }, [open, resetState]);

    useEffect(() => {
        if (!open || !variant) return;
        setLevel(initialLevel);
        setSearchInput("");
        setDebouncedSearch("");
        setPageNo(1);
        isFirstSearchSync.current = true;
    }, [open, variant, initialLevel]);

    useEffect(() => {
        if (!open || !variant) return;
        const t = setTimeout(() => {
            setDebouncedSearch(searchInput);
            if (isFirstSearchSync.current) {
                isFirstSearchSync.current = false;
            } else {
                setPageNo(1);
            }
        }, 300);
        return () => clearTimeout(t);
    }, [searchInput, open, variant]);

    useEffect(() => {
        if (!open || !variant) return;
        fetchPage({ page: pageNo, searchTerm: debouncedSearch, levelVal: level });
    }, [open, variant, pageNo, debouncedSearch, level, fetchPage]);

    return {
        list,
        info,
        loading,
        error,
        level,
        searchInput,
        setSearchInput,
        onLevelChange,
        refresh,
        pageNo,
        totalPages,
        onPrev,
        onNext,
        onJump,
    };
}
